<template>
  <div class="position-relative">
    <div class="widget">
      <div class="widget-content" v-if="importing">
        <AlertBlock :text="$t('training.rncp.alert.importing')" type="success" />
      </div>
      <div class="widget-content" v-if="enriching">
        <AlertBlock :text="$t('training.rncp.alert.enriching')" type="success" />
      </div>
      <template v-if="hasBeenImportedOnce && referential.categories && Object.keys(referential.categories).length">
        <div class="widget-heading">
          <h2 class="textStyle">
            <FontAwesomeIcon icon="fa-light fa-medal" />
            {{ $tc("studea_manager.trainingSkillCategory.title", 2) }}
          </h2>
          <div class="bloc-actions">
            <span class="badge bg-info" v-if="referential.categories"
              >{{ Object.keys(referential.categories).length }}
              {{ $tc("studea_manager.trainingSkillCategory.block", 2) }}</span
            >
            <button
              class="btn btn-icon ms-2"
              @click="addEditCategory({ origin: 3 })"
              v-tooltip="{
                title: stringManager.globalTranslation('add', 'training-skill-category', 'studea_manager', 1, true),
              }"
            >
              <FontAwesomeIcon icon="fa-light fa-plus" />
            </button>
          </div>
        </div>
        <div class="widget-content">
          <div id="skill-category-accordion" class="accordion accordion-icons">
            <template v-if="referential.categories && Object.keys(referential.categories).length">
              <VueDraggableNext
                :animation="200"
                :delay="200"
                :delay-on-touch-only="true"
                :list="referential.categories"
                handle=".accordion__grip"
                @change="sort"
              >
                <template v-for="category in referential.categories" :key="category.id">
                  <TrainingSkillCategoryComponent
                    :category="category"
                    :targetChoices="targetChoices"
                    :ia="isIaActive"
                    @editCategory="addEditCategory"
                    @addSkill="addEditSkill"
                    @editSkill="addEditSkill"
                    @editMission="addEditMission"
                    @addMission="addEditMission"
                    @removed="removed"
                    @hideComplete="hideComplete"
                    @accepted="accepted"
                    @refreshSuggestions="refreshSuggestions"
                    @showDetails="showDetails"
                  />
                </template>
              </VueDraggableNext>
            </template>
            <AlertBlock v-else-if="!importing" :text="$t('training.rncp.alert.empty_block')" type="info" />
          </div>
        </div>
      </template>
      <div class="widget-content" v-else-if="!importing">
        <AlertBlock :text="$t('training.rncp.alert.rncp_skill_import')" type="info mb-0" />
      </div>
    </div>
    <BlockLoader :loading="loading || importing || enriching" />
  </div>
  <ModalFormComponent :models="models" :title="modalTitle" @submit="modalSubmit" />
  <TrainingRncpSourceModalComponent @hidden="sourceHidden" :object="sourceToDisplay" />
</template>

<script>
import BlockLoader from "@/components/blocks/BlockLoader.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import TrainingSkillCategoryComponent from "@/components/training/skill/TrainingSkillCategoryComponent.vue";
import { VueDraggableNext } from "vue-draggable-next";
import { trainingManager } from "@/services/training/training-manager";
import ModalFormComponent from "@/components/form/ModalFormComponent.vue";
import { formManager } from "@/services/form/form-manager";
import { useNotificationStore } from "@/store/notifications/notification";
import { mapState } from "pinia";
import AlertBlock from "@/components/blocks/AlertBlock.vue";
import { notificationManager } from "@/services/utilities/notification-manager";
import { i18n } from "@/i18n";
import { stringManager } from "@/services/utilities/string-manager";
import TrainingRncpSourceModalComponent from "@/components/training/rncp/TrainingRncpSourceModalComponent.vue";
import { trainingSkillConstante } from "@/constantes/trainingSkill";

export default {
  name: "TrainingSkillComponent",
  components: {
    TrainingRncpSourceModalComponent,
    AlertBlock,
    ModalFormComponent,
    VueDraggableNext,
    TrainingSkillCategoryComponent,
    FontAwesomeIcon,
    BlockLoader,
  },
  emits: ["refresh", "error"],
  props: {
    hasBeenImportedOnce: {
      type: Boolean,
      required: true,
    },
    hasBeenEnrichedOnce: {
      type: Boolean,
      required: true,
    },
    importing: {
      type: Boolean,
      required: true,
    },
    enriching: {
      type: Boolean,
      required: true,
    },
    active: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      referential: {},
      models: [],
      modalTitle: "",
      modalType: "",
      modalCategoryId: null,
      modalObjectId: null,
      modalObject: null,
      modalLoadObject: null,
      targetChoices: [],
      sourceToDisplay: null,
    };
  },
  computed: {
    stringManager() {
      return stringManager;
    },
    isIaActive() {
      return !!this.hasBeenEnrichedOnce;
    },

    ...mapState(useNotificationStore, {
      status: (store) => store.status,
      messages: (store) => store.messages,
    }),
  },
  watch: {
    status() {
      this.checkMessagesState(this.messages, true);
    },

    $route() {
      this.init();
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      if (this.active) {
        this.getData();
        this.checkMessagesState(this.messages);
      }
    },

    checkMessagesState(messages, needInit = false) {
      let messageId;
      if (sessionStorage.getItem("studea-training-" + this.$route.params.trainingId + "-rncpImportMessageId")) {
        messageId = sessionStorage.getItem("studea-training-" + this.$route.params.trainingId + "-rncpImportMessageId");
      } else if (
        sessionStorage.getItem("studea-training-" + this.$route.params.trainingId + "-rncpEnrichingMessageId")
      ) {
        messageId = sessionStorage.getItem(
          "studea-training-" + this.$route.params.trainingId + "-rncpEnrichingMessageId",
        );
      }
      const message = Object.values(messages).find((message) => message.id === messageId);
      if (message) {
        if (message.status === "success") {
          this.$emit("refresh");
          if (needInit) {
            this.init();
          }
        } else if (message.status === "error") {
          this.$emit("error");
        }
      }
    },

    clearData() {
      this.referential.categories = {};
    },

    addEditCategory(payload) {
      this.modalType = trainingSkillConstante.OBJECT_TYPE_SKILL + "/category";
      this.modalTitle = i18n.global.tc("global." + (payload.category?.id ? "edit" : "add") + ".title", 1, {
        name: i18n.global.tc("studea_manager.trainingSkillCategory.title", 1).toLowerCase(),
      });
      this.modalObjectId = payload.category?.id;
      this.modalObject = payload.category;
      this.modalLoadObject = payload.category;
      this.form();
    },

    addEditSkill(payload) {
      this.modalType = trainingSkillConstante.OBJECT_TYPE_SKILL;
      this.modalTitle = i18n.global.tc("global." + (payload.skill?.id ? "edit" : "add") + ".title", 2, {
        name: i18n.global.tc("studea_manager.skill.title", 1).toLowerCase(),
      });
      this.modalCategoryId = payload.category?.id;
      this.modalObjectId = payload.skill?.id;
      this.modalObject = payload.skill;
      this.modalLoadObject = payload.skill?.id ? payload.skill : null;
      this.form();
    },

    addEditMission(payload) {
      this.modalType = trainingSkillConstante.OBJECT_TYPE_MISSION;
      this.modalTitle = i18n.global.tc("global." + (payload.mission?.id ? "edit" : "add") + ".title", 2, {
        name: i18n.global.tc("studea_manager.mission.title", 1).toLowerCase(),
      });
      this.modalCategoryId = payload.category?.id;
      this.modalObjectId = payload.mission?.id;
      this.modalObject = payload.mission;
      this.modalLoadObject = payload.mission?.id ? payload.mission : null;
      this.form();
    },

    async form() {
      this.models = [];
      this.setLoading();
      this.models = await trainingManager.form(
        this.modalType,
        this.modalObjectId,
        false,
        this.$route.params.trainingId,
      );
      this.cancelLoading();
    },

    sort(e) {
      if (e.moved) {
        const id = e.moved.element.id;
        const position = e.moved.newIndex;
        trainingManager.sort(
          id,
          this.$route.params.trainingId,
          position,
          trainingSkillConstante.OBJECT_TYPE_SKILL + "/category",
          "training-skill-category",
        );
      }
    },

    async getData() {
      this.loading = true;
      this.referential = await trainingManager.get(
        this.$route.params.trainingId,
        trainingSkillConstante.OBJECT_TYPE_SKILL + "/list",
      );
      this.setTargetChoices();
      this.loading = false;
    },

    setTargetChoices() {
      let targetChoices = [];
      if (this.referential && this.referential.categories && Object.keys(this.referential.categories).length) {
        Object.values(this.referential.categories).forEach((category) => {
          targetChoices.push({
            label: (category.code ? category.code + " " : "") + category.name,
            value: category.id,
            data: category.id,
          });
        });
      }
      this.targetChoices = targetChoices;
    },

    setLoading() {
      let category;
      if (
        this.modalCategoryId &&
        !this.modalObjectId &&
        this.modalType !== trainingSkillConstante.OBJECT_TYPE_SKILL + "/category"
      ) {
        {
          category = this.getCategory(this.modalCategoryId);
          if (category) {
            category[this.modalType + "ColumnLoading"] = true;
          }
        }
      }
      if (this.modalType === trainingSkillConstante.OBJECT_TYPE_SKILL + "/category" && !this.modalObjectId) {
        this.loading = true;
      }
      if (this.modalLoadObject) {
        this.modalLoadObject["loading"] = true;
      }
    },

    cancelLoading() {
      let category = this.getCategory(this.modalCategoryId);
      if (category) {
        category[this.modalType + "ColumnLoading"] = false;
      }

      if (this.modalLoadObject) {
        this.modalLoadObject["loading"] = false;
      }
      this.loading = false;
    },

    async modalSubmit() {
      const params = formManager.processForm(this.models);
      if (this.modalCategoryId && !this.modalObjectId) {
        {
          params["skillCategory"] = this.modalCategoryId;
        }
      }

      this.setLoading();

      const response = await (this.modalObjectId
        ? trainingManager.put(this.modalObjectId, this.$route.params.trainingId, this.modalType, params)
        : trainingManager.post(this.$route.params.trainingId, this.modalType, params));

      const error = response.hasError || (response.success !== undefined && !response.success);

      if (!error && this.modalObject) {
        this.updateObjectWithResponse(response);
      } else if (!error) {
        this.objectPush(response.responses[0].object, this.modalCategoryId);
      }

      const tradPrefix =
        this.modalType === trainingSkillConstante.OBJECT_TYPE_SKILL + "/category"
          ? "trainingSkillCategory"
          : this.modalType;
      const tradGender = stringManager.gender(tradPrefix);
      const message = response?.response?.data?.message;

      notificationManager.showNotification(
        !error ? "success" : "error",
        !error
          ? i18n.global.tc("global." + (this.modalObjectId ? "edit" : "add") + ".success", tradGender, {
              name: i18n.global.tc("studea_manager." + tradPrefix + ".title", 1),
            })
          : message
            ? message
            : i18n.global.t("signed_error"),
      );
      this.cancelLoading();
      this.clearModal();
    },

    updateObjectWithResponse(response) {
      if (response && response.responses && response.responses[0] && response.responses[0].object) {
        this.getKeysToUpdate().forEach((key) => {
          if (key === "skillCategory") {
            const targetCategoryId = response.responses[0].object["skillCategory"]["id"];
            if (targetCategoryId === this.modalCategoryId) {
              return;
            }
            this.objectPush(response.responses[0].object, targetCategoryId);
            const category = this.getCategory(this.modalCategoryId);
            const key = this.modalType + "sAccepted";
            if (category) {
              category[key] = this.objectRemoval(category[key], response.responses[0].object.id);
            }
          } else {
            this.modalObject[key] = response.responses[0].object[key];
          }
        });
      }
    },

    getKeysToUpdate() {
      switch (this.modalType) {
        case trainingSkillConstante.OBJECT_TYPE_SKILL + "/category":
          return ["name", "code"];
        case trainingSkillConstante.OBJECT_TYPE_SKILL:
          return ["name", "code", "skillCategory"];
        case trainingSkillConstante.OBJECT_TYPE_MISSION:
          return ["name", "skillCategory"];
        default:
          return [];
      }
    },

    objectPush(object, categoryId = null) {
      if (this.modalType === trainingSkillConstante.OBJECT_TYPE_SKILL + "/category") {
        this.referential.categories.push(object);
      } else {
        const category = this.getCategory(categoryId);
        const key = this.modalType + "sAccepted";
        if (category && key) {
          category[key].push(object);
        }
      }
    },

    getCategory(categoryId) {
      return this.referential.categories
        ? Object.values(this.referential.categories).find((category) => category.id === categoryId)
        : null;
    },

    clearModal() {
      this.models = [];
      this.modalTitle = "";
      this.modalType = "";
      this.modalCategoryId = null;
      this.modalObjectId = null;
      this.modalObject = null;
      this.modalLoadObject = null;
    },

    removed(payload) {
      console.log("removed", payload);
      let category;
      if (payload.objectId) {
        category = this.getCategory(payload.objectId);
        if (category) {
          category.removed = true;
        }
      }
    },

    hideComplete(payload) {
      console.log("hideComplete", payload);
      let category;
      if (payload.categoryId) {
        category = this.getCategory(payload.categoryId);
        category[payload.key] = this.objectRemoval(category[payload.key], payload.objectId);
        return;
      }

      this.referential[payload.key] = this.objectRemoval(this.referential[payload.key], payload.objectId);
    },

    refreshSuggestions(payload) {
      let category;
      if (payload.categoryId) {
        category = this.getCategory(payload.categoryId);
        category[payload.key] = payload.objects;
      }
    },

    objectRemoval(object, id) {
      return object.filter((item) => id !== item.id);
    },

    async accepted(payload) {
      if (!payload || typeof payload !== "object" || !payload.object || typeof payload.object !== "object") {
        return;
      }
      if (!payload.type || !payload.targetCategoryId || !payload.baseCategoryId) {
        return;
      }

      payload.object["loading"] = true;
      const done = await trainingManager.accept(
        payload.object.id,
        payload.targetCategoryId,
        this.$route.params.trainingId,
        payload.type,
        payload.type,
      );
      if (done.success) {
        const acceptedKey = payload.type + "sAccepted";
        const suggestedKey = payload.type + "sSuggested";
        const targetCategory = this.getCategory(payload.targetCategoryId);
        const baseCategory = this.getCategory(payload.baseCategoryId);

        if (targetCategory && baseCategory) {
          targetCategory[acceptedKey].push(payload.object);
          baseCategory[suggestedKey] = this.objectRemoval(baseCategory[suggestedKey], payload.object.id);
          payload.object["loading"] = false;
        }
      }
    },

    async showDetails(payload) {
      payload.object.loading = true;
      const res = await trainingManager.get(this.$route.params.trainingId, payload.type + "/detail", payload.object.id);
      payload.object.loading = false;
      res["name"] = payload.object.name;
      res["type"] = payload.type;
      this.sourceToDisplay = res;
    },

    sourceHidden() {
      this.sourceToDisplay = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.bloc-actions {
  min-width: 86px;
}

.widget-content,
.widget-heading {
  @include up-to-md {
    padding: 20px 10px !important;
  }
}

.widget-content {
  padding-bottom: 0 !important;
}
</style>
